import type { InstantConfigRepository } from "instant-config";
import type { PageTargeting } from "../../../../config/page";

/**
 * Configuration properties that Audigent will use from Instant Config.
 */
export type AudigentPluginConfig = Partial<
  Pick<
    InstantConfigRepository,
    "icAudigent" | "icAudigentSegmentLimit" | "icForceDefaultAuSegOnAppNexus"
  >
>;

/**
 * Key for the audigent segments added to gpt targeting.
 */
export const AudigentSegmentKeyvalue = "AU_SEG";

/**
 * The event that will be emitted on the document when window.au_seg is updated.
 */
export const AuSegReadyEvent = "auSegReady";

/**
 * Tracking eventNames to send to the tracking server for Audigent. Audigent plugin will currently
 * track when the two scripts load, matches and segments, as well as when the segments become
 * available and are added to gpt tracking.
 */
export enum AudigentTrackingEventName {
  /** Tracking event for when the Audigent matches script has loaded.*/
  MATCHES_SCRIPT_LOADED = "audigent_matches_library_loaded",
  /** Tracking event for when the Audigent matches script has failed to load. */
  MATCHES_SCRIPT_ERROR_LOADING = "audigent_matches_library_error_loading",
  /** Tracking event for when the Audigent segments script has loaded. */
  SEGMENTS_SCRIPT_LOADED = "audigent_segment_library_loaded",
  /** Tracking event for when the Audigent segments script has failed to load. */
  SEGMENTS_SCRIPT_ERROR_LOADING = "audigent_segment_library_error_loading",
  /**
   * Tracking event for when the window.au_seg object and the AU_SEG keyvalue data is ready and has
   * been added to gpt.
   */
  SEGMENTS_READY = "audigent_segments_ready",
}

/**
 * Type of the Gpt Keyvalue for AU_SEG that will be added. This can be either a list of segment
 * values (like `"AU_SEG_AGE_18-24"` or `"AU_AUD_PXSU75"`), or when no segments are provided yet,
 * the default value of `"-1"`.
 */
export type AudigentSegments = string[] | "-1";

/**
 * The shape of the `window.au_seg` object Audigent uses to provide the segmenets.
 */
export interface AudigentWindowSegments {
  segments: string[];
}

declare global {
  interface Window {
    /**
     * Audigent will place the segments once retrieved on this window property. This is provided
     * by the segments script, which will set the value from the script itself, try to load any
     * cached values from local storage, and make a request for updated values as well. Once updated
     * on this window property, Audigent will emit the {@link AuSegReadyEvent} on the document.
     */
    au_seg?: AudigentWindowSegments;
  }
}

/**
 * Add Audigent Targeting Values to PageTargeting type for use within the
 * Audigent Plugin Code.
 *
 * If any external plugins require access to this keyvalue, they will need to
 * import PageTargeting from this file.
 */
declare module "../../../../config/page" {
  interface PageTargeting {
    [AudigentSegmentKeyvalue]?: AudigentSegments;
  }
}

export type { PageTargeting };
